import { clientRouteHeaders } from '@/lib/client-route';
import { legacyTokenKeys } from '@/lib/legacy-storage';
import { recordHttpFailure } from '@/lib/error-report/log-buffer';
import axios, { AxiosError, AxiosInstance } from 'axios';

// **O fallback tem de ser relativo.** `web/.env` não é versionado: num clone
// fresco a variável é `undefined` e o fallback é o que fica a valer. Enquanto
// foi `http://localhost:8000/api/v1`, apontava para a API de UM cliente em
// absoluto — a app de qualquer marca, `dev:epel` incluída, saltava o proxy do
// servidor de dev e ia buscar os dados ao outro. Relativo fica na origem da
// app, e a origem já sabe de quem é.
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "/api/v1";
const TOKEN_STORAGE_KEY = "auth_token";
// A chave antiga levava o nome de um cliente (até 2026-09-01). Continua aqui só para ser APAGADA: quem já a tem no
// disco fica com ela para sempre se ninguém a limpar, e a sessão real vem do cookie. O nome deriva da marca da build
// (ver `lib/legacy-storage.ts`), não está escrito aqui.
const LEGACY_TOKEN_KEYS = legacyTokenKeys();

/**
 * Token storage utilities - DEPRECATED for new code
 *
 * Authentication has been migrated to HttpOnly cookies for security.
 * These utilities are kept for backwards compatibility only:
 * - tokenStorage.clear() is used to clean up legacy tokens
 * - Old tokens in localStorage are still sent as fallback
 *
 * New authentication flow:
 * 1. Login sets HttpOnly cookie (JavaScript cannot access it)
 * 2. All requests include withCredentials: true to send cookies
 * 3. Backend reads token from cookie (prioritized) or Authorization header
 * 4. XSS attacks cannot steal tokens since they're in HttpOnly cookies
 */
export const tokenStorage = {
    get: () =>
        localStorage.getItem(TOKEN_STORAGE_KEY) ??
        // Um token gravado com o nome antigo ainda serve enquanto não expirar:
        // ler os dois evita deslogar quem esteja a meio de uma sessão.
        LEGACY_TOKEN_KEYS.reduce<string | null>((achado, k) => achado ?? localStorage.getItem(k), null),
    set: (token: string) => localStorage.setItem(TOKEN_STORAGE_KEY, token),  // Deprecated - not used for new logins
    clear: () => {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        LEGACY_TOKEN_KEYS.forEach((k) => localStorage.removeItem(k));
    },
};

// Global event system for handling 401 errors
type UnauthorizedHandler = () => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;

export const apiAuth = {
    setUnauthorizedHandler: (handler: UnauthorizedHandler) => {
        unauthorizedHandler = handler;
    },
    clearUnauthorizedHandler: () => {
        unauthorizedHandler = null;
    },
};

// Create axios instance with default configuration
const axiosInstance: AxiosInstance = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,  // Include HttpOnly cookies in all requests (CRITICAL for security)
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor - adds Authorization header if legacy token exists
axiosInstance.interceptors.request.use(
    (config) => {
        // For backwards compatibility, include Authorization header if token exists in localStorage
        const token = tokenStorage.get();
        if (token && !config.headers.Authorization) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // Em que ecrã estava a pessoa: alimenta o «onde» da auditoria de IA (só o caminho).
        for (const [name, value] of Object.entries(clientRouteHeaders())) {
            config.headers.set(name, value);
        }

        // For FormData (file uploads), remove Content-Type to let axios set it with boundary
        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

/**
 * Renovação silenciosa da sessão.
 *
 * O access token dura 30 minutos; o refresh token, 90 dias. Quando o primeiro
 * expira, o pedido volta 401 — e ANTES isso atirava a pessoa para o ecrã de
 * login. Era esse o motivo de andar sempre a fazer login: não era a sessão que
 * acabava, era a app que desistia ao primeiro 401.
 *
 * IMPORTANTE — a API tem de estar na MESMA ORIGEM da app (por isso
 * `VITE_API_BASE_URL` é relativo: `/api/v1`). A cookie do refresh token é
 * SameSite=Lax; se a API viver noutro subdomínio, o browser não a envia neste
 * POST e a renovação devolve 401 SEMPRE — a sessão cai de 30 em 30 minutos e
 * parece que "a plataforma foi abaixo". Foi exactamente isso que aconteceu
 * enquanto a app falava com apiafisocks.bitsapiens.io. Se um dia voltarem a
 * apontar isto para outro domínio, este ficheiro é o primeiro sítio a olhar.
 *
 * Agora tenta-se renovar e repete-se o pedido. Só se a renovação falhar é que
 * há logout — aí a sessão morreu mesmo (revogada, expirada, ou conta desativada).
 *
 * `refreshInFlight` garante UMA renovação de cada vez. Sem isto, um ecrã com
 * seis pedidos em paralelo dispararia seis renovações; como o refresh token roda
 * a cada uso, cinco delas chegariam com um token já consumido e o servidor
 * leria isso como reutilização — matando a sessão que se estava a tentar salvar.
 */
let refreshInFlight: Promise<void> | null = null;

const refreshSession = (): Promise<void> => {
    if (!refreshInFlight) {
        refreshInFlight = axios
            .post(`${API_BASE_URL}/auth/refresh`, null, { withCredentials: true })
            .then(() => undefined)
            .finally(() => {
                refreshInFlight = null;
            });
    }
    return refreshInFlight;
};

/** Rotas de autenticação onde um 401 é a resposta, não um acidente. */
const isAuthRoute = (url?: string) =>
    !!url && (url.includes('/auth/login') || url.includes('/auth/refresh') || url.includes('/auth/logout'));

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * O servidor está de facto em baixo, ou é só o container a reiniciar?
 *
 * Num deploy a API fica indisponível uns segundos. Nessa janela os pedidos
 * falham sem resposta (erro de rede) ou com 502/503 do nginx. Nenhum desses
 * casos significa que a sessão acabou — significa "tenta outra vez daqui a
 * pouco". Um 500 já é a aplicação a responder com um erro seu, e repetir não o
 * resolve.
 */
const isServerRestarting = (error: AxiosError) => {
    const status = error.response?.status;
    if (status === undefined) return error.code !== 'ECONNABORTED'; // sem resposta = rede/servidor em baixo
    return status === 502 || status === 503 || status === 504;
};

const MAX_RETRIES = 3;

/**
 * Só se repete o que é seguro repetir. Um 502 não diz se o servidor chegou a executar o pedido: repetir uma leitura
 * é inofensivo, repetir uma escrita («Iniciar produção», criar encomenda) pode duplicá-la — e, se foi ela que fez o
 * servidor cair, derruba-o outra vez em cada tentativa, com o botão a rodar. A escrita que falha mostra o erro e a
 * pessoa decide.
 */
const isIdempotent = (method?: string) => ['get', 'head', 'options'].includes((method ?? 'get').toLowerCase());

/**
 * Nas rotas de IA um 502/504 é o FORNECEDOR que falhou (a aplicação respondeu), não o servidor a reiniciar: repetir
 * só faz a pessoa esperar mais de um minuto por um erro que já tinha, e paga o pedido ao modelo outra vez.
 */
const isAiCall = (url?: string) => !!url && /(^|\/)ai(\/|\?|$)/.test(url.split('?')[0]);

// Response interceptor - handles 401 errors globally
axiosInstance.interceptors.response.use(
    (response) => {
        // Success response - return as is
        return response;
    },
    async (error: AxiosError) => {
        // Fica no registo que acompanha um relatório de erro (nunca o corpo do pedido).
        recordHttpFailure({
            method: error.config?.method,
            url: error.config?.url,
            status: error.response?.status,
            message: error.code,
        });
        const config = error.config as (typeof error.config & { _retried?: boolean; _attempts?: number }) | undefined;

        // 401 → tentar renovar a sessão UMA vez e repetir o pedido original.
        if (error.response?.status === 401 && config && !config._retried && !isAuthRoute(config.url)) {
            config._retried = true;
            try {
                await refreshSession();
                return await axiosInstance(config);
            } catch {
                // A renovação falhou: a sessão acabou mesmo.
                if (unauthorizedHandler) unauthorizedHandler();
                return Promise.reject(error);
            }
        }

        // Servidor a reiniciar (deploy): esperar e tentar de novo, com espera
        // crescente. Cobre a janela de indisponibilidade sem o utilizador dar
        // por ela — e sem o mandar para o login por causa dela. Só leituras.
        if (config && isIdempotent(config.method) && isServerRestarting(error) && !isAiCall(config.url)) {
            const attempts = config._attempts ?? 0;
            if (attempts < MAX_RETRIES) {
                config._attempts = attempts + 1;
                await sleep(500 * 2 ** attempts); // 0.5s, 1s, 2s
                return axiosInstance(config);
            }
        }

        // 401 numa rota de auth, ou depois de a renovação já ter falhado.
        if (error.response?.status === 401 && !isAuthRoute(config?.url) && unauthorizedHandler) {
            unauthorizedHandler();
        }

        // Extract error message and validation details from response
        let message = 'An error occurred';
        const data = error.response?.data as any;

        if (error.response?.data) {
            // Handle Pydantic validation errors (422)
            if (Array.isArray(data.detail)) {
                // For validation errors, extract first error message
                const firstError = data.detail[0];
                if (firstError?.msg) {
                    message = firstError.msg;
                }
            } else if (typeof data.detail === 'string') {
                message = data.detail;
            } else if (data.detail?.message) {
                message = data.detail.message;
            } else if (data.detail) {
                message = JSON.stringify(data.detail);
            } else if (data.message) {
                message = data.message;
            }
        } else if (error.message) {
            message = error.message;
        }

        // Create enhanced error with status and full validation details
        const enhancedError = new Error(message);
        (enhancedError as any).status = error.response?.status;
        (enhancedError as any).payload = error.response?.data;
        // Preserve full validation details for form handling
        (enhancedError as any).detail = data?.detail;

        return Promise.reject(enhancedError);
    }
);

// Legacy wrapper function for backwards compatibility
export interface ApiRequestOptions {
    method?: string;
    body?: unknown;
    skipAuth?: boolean;
    headers?: Record<string, string>;
    /** `blob` para descarregar ficheiros: o corpo de um erro também vem em blob (ver `blobErrorMessage`). */
    responseType?: 'blob';
    /** Progresso do envio, em bytes; quem quer percentagem usa `api.upload`. */
    onUploadProgress?: (e: { loaded: number; total?: number }) => void;
}

export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
    const { method = 'GET', body, headers, responseType, onUploadProgress } = options;

    // For FormData (file uploads), don't set Content-Type - let axios auto-detect it
    const isFormData = body instanceof FormData;

    const config = {
        method,
        url: path,
        data: body,
        responseType,
        onUploadProgress,
        headers: isFormData
            ? { ...headers }  // Don't include Content-Type for FormData
            : headers,
    };

    // Remove Content-Type for FormData so axios can set it with boundary
    if (isFormData) {
        delete (config.headers as any)?.['Content-Type'];
    }

    const response = await axiosInstance.request<T>(config);
    return response.data;
}

/**
 * Uma página de uma listagem do servidor com o total que cabe nos filtros (`X-Total-Count`).
 * Sem o cabeçalho (rota que não pagina) o total é o que veio.
 */
export async function apiRequestPage<T>(path: string): Promise<{ items: T[]; total: number }> {
    const response = await axiosInstance.get<T[]>(path);
    const total = Number(response.headers?.['x-total-count']);
    return { items: response.data, total: Number.isFinite(total) && response.headers?.['x-total-count'] !== undefined ? total : response.data.length };
}

/**
 * Enhanced axios client that automatically unwraps response data
 * This maintains the axios API (get, post, etc) but returns T instead of AxiosResponse<T>
 */
export const apiClient = {
    get: async <T = any>(url: string, config?: any): Promise<T> => {
        const response = await axiosInstance.get<T>(url, config);
        return response.data;
    },
    post: async <T = any>(url: string, data?: any, config?: any): Promise<T> => {
        const response = await axiosInstance.post<T>(url, data, config);
        return response.data;
    },
    put: async <T = any>(url: string, data?: any, config?: any): Promise<T> => {
        const response = await axiosInstance.put<T>(url, data, config);
        return response.data;
    },
    patch: async <T = any>(url: string, data?: any, config?: any): Promise<T> => {
        const response = await axiosInstance.patch<T>(url, data, config);
        return response.data;
    },
    delete: async <T = any>(url: string, config?: any): Promise<T> => {
        const response = await axiosInstance.delete<T>(url, config);
        return response.data;
    },
    // Expose the raw axios instance for advanced use cases
    axios: axiosInstance,
};

/**
 * Get full URL for storage files (images, PDFs, etc.)
 * Handles both relative paths from API and data URLs
 */
export function getStorageUrl(path: string | null | undefined): string {
    if (!path) return "";

    // If it's a data URL (base64), return as-is
    if (path.startsWith("data:")) return path;

    // If it's already a full URL, return as-is
    if (path.startsWith("http://") || path.startsWith("https://")) return path;

    // Remove /api/v1 from base URL to get the server base URL
    const serverBaseUrl = API_BASE_URL.replace(/\/api\/v1$/, "");

    // Ensure path starts with /
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;

    return `${serverBaseUrl}${normalizedPath}`;
}

